// 预产期计算相关函数

export interface PregnancyData {
  lastPeriodDate: string;
}

export interface PregnancyResult {
  dueDate: Date;
  currentWeek: number;
  currentDay: number;
  daysPregnant: number;
  daysRemaining: number;
  trimester: number;
  conceptionDate: Date;
}

export interface ValidationResult {
  isValid: boolean;
  errors: string[];
}

/**
 * 验证预产期计算数据
 */
export function validatePregnancyData(data: PregnancyData): ValidationResult {
  const errors: string[] = [];

  // 验证末次月经日期
  if (!data.lastPeriodDate) {
    errors.push('请选择末次月经开始日期');
  } else {
    const lastPeriod = new Date(data.lastPeriodDate);
    const today = new Date();
    const maxDate = new Date();
    maxDate.setMonth(maxDate.getMonth() - 10); // 最多10个月前

    if (lastPeriod > today) {
      errors.push('末次月经日期不能是未来日期');
    } else if (lastPeriod < maxDate) {
      errors.push('末次月经日期不能超过10个月前');
    }
  }

  return {
    isValid: errors.length === 0,
    errors
  };
}

/**
 * 计算预产期和孕期信息
 * 基于Naegele's Rule：预产期 = 末次月经日期 + 280天
 */
export function calculatePregnancy(data: PregnancyData): PregnancyResult {
  const lastPeriod = new Date(data.lastPeriodDate);
  const today = new Date();

  // 计算预产期（末次月经 + 280天）
  const dueDate = new Date(lastPeriod);
  dueDate.setDate(dueDate.getDate() + 280);

  // 计算受孕日期（通常在末次月经后14天）
  const conceptionDate = new Date(lastPeriod);
  conceptionDate.setDate(conceptionDate.getDate() + 14);

  // 计算已怀孕天数
  const daysPregnant = Math.floor((today.getTime() - lastPeriod.getTime()) / (1000 * 60 * 60 * 24));

  // 计算当前孕周和天数
  const currentWeek = Math.floor(daysPregnant / 7);
  const currentDay = daysPregnant % 7;

  // 计算距离预产期的天数
  const daysRemaining = Math.ceil((dueDate.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));

  // 确定孕期阶段（1-3期）
  let trimester: number;
  if (currentWeek <= 12) {
    trimester = 1; // 孕早期
  } else if (currentWeek <= 27) {
    trimester = 2; // 孕中期
  } else {
    trimester = 3; // 孕晚期
  }

  return {
    dueDate,
    currentWeek,
    currentDay,
    daysPregnant,
    daysRemaining,
    trimester,
    conceptionDate
  };
}

/**
 * 获取孕期阶段信息
 */
export function getTrimesterInfo(trimester: number) {
  switch (trimester) {
    case 1:
      return {
        name: '孕早期',
        period: '1-12周',
        description: '胚胎器官形成的关键时期',
        keyPoints: [
          '胚胎着床和器官发育',
          '可能出现早孕反应',
          '流产风险相对较高',
          '需要补充叶酸'
        ],
        tips: [
          '补充叶酸400-800微克/天',
          '避免接触有害物质',
          '戒烟戒酒',
          '避免剧烈运动',
          '保持充足睡眠',
          '定期产检'
        ]
      };
    case 2:
      return {
        name: '孕中期',
        period: '13-27周',
        description: '相对稳定的时期，胎儿快速发育',
        keyPoints: [
          '早孕反应逐渐消失',
          '胎儿快速发育',
          '可以感受到胎动',
          '进行重要的产前检查'
        ],
        tips: [
          '均衡营养，增加蛋白质摄入',
          '适量运动，如孕妇瑜伽',
          '进行胎教活动',
          '监测胎动',
          '进行大排畸检查',
          '控制体重增长'
        ]
      };
    case 3:
      return {
        name: '孕晚期',
        period: '28-40周',
        description: '胎儿成熟，准备分娩',
        keyPoints: [
          '胎儿肺部发育成熟',
          '可能出现假性宫缩',
          '需要准备分娩',
          '密切监测胎儿状况'
        ],
        tips: [
          '准备待产包',
          '学习分娩和育儿知识',
          '密切监测胎动',
          '注意宫缩频率',
          '保持心情愉快',
          '按时产检'
        ]
      };
    default:
      return null;
  }
}

/**
 * 获取孕期里程碑
 */
export function getPregnancyMilestones() {
  return [
    {
      week: 4,
      title: '胚胎着床',
      description: '胚胎成功着床，开始分泌HCG'
    },
    {
      week: 8,
      title: '器官形成',
      description: '主要器官开始形成，心脏开始跳动'
    },
    {
      week: 12,
      title: '孕早期结束',
      description: '胎儿器官基本形成，流产风险降低'
    },
    {
      week: 16,
      title: '性别确定',
      description: '可以通过B超确定胎儿性别'
    },
    {
      week: 20,
      title: '孕期中点',
      description: '进行大排畸检查，开始感受胎动'
    },
    {
      week: 24,
      title: '胎儿存活期',
      description: '胎儿在医疗帮助下有存活可能'
    },
    {
      week: 28,
      title: '孕晚期开始',
      description: '胎儿存活率大幅提高'
    },
    {
      week: 32,
      title: '肺部发育',
      description: '胎儿肺部快速发育'
    },
    {
      week: 36,
      title: '接近足月',
      description: '胎儿基本发育成熟'
    },
    {
      week: 37,
      title: '足月妊娠',
      description: '胎儿发育成熟，随时可能分娩'
    },
    {
      week: 40,
      title: '预产期',
      description: '预计分娩日期'
    },
    {
      week: 42,
      title: '过期妊娠',
      description: '需要医疗干预'
    }
  ];
}

/**
 * 计算胎儿体重估算（仅供参考）
 */
export function estimateFetalWeight(weeks: number): { weight: number; length: number } {
  // 这是一个简化的估算公式，实际应该通过B超测量
  let weight = 0; // 克
  let length = 0; // 厘米

  if (weeks >= 8) {
    // 8周后开始有明显的体重和身长
    if (weeks <= 20) {
      weight = Math.pow(weeks - 7, 2) * 10;
      length = (weeks - 7) * 2;
    } else if (weeks <= 36) {
      weight = 300 + (weeks - 20) * 150;
      length = 25 + (weeks - 20) * 1.5;
    } else {
      weight = 2700 + (weeks - 36) * 100;
      length = 49 + (weeks - 36) * 0.5;
    }
  }

  return {
    weight: Math.round(weight),
    length: Math.round(length * 10) / 10
  };
}

/**
 * 格式化日期为中文
 */
export function formatDateChinese(date: Date): string {
  return date.toLocaleDateString('zh-CN', {
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  });
}

/**
 * 计算两个日期之间的天数
 */
export function daysBetween(date1: Date, date2: Date): number {
  const diffTime = Math.abs(date2.getTime() - date1.getTime());
  return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
}

/**
 * 获取孕期阶段的颜色
 */
export function getTrimesterColor(trimester: number): string {
  switch (trimester) {
    case 1:
      return '#f59e0b'; // 橙色
    case 2:
      return '#10b981'; // 绿色
    case 3:
      return '#8b5cf6'; // 紫色
    default:
      return '#6b7280'; // 灰色
  }
}

/**
 * 检查是否为高危妊娠（简单判断）
 */
export function checkHighRiskPregnancy(data: PregnancyData, maternalAge?: number): {
  isHighRisk: boolean;
  riskFactors: string[];
} {
  const riskFactors: string[] = [];
  
  // 年龄因素
  if (maternalAge) {
    if (maternalAge < 18) {
      riskFactors.push('年龄过小（<18岁）');
    } else if (maternalAge >= 35) {
      riskFactors.push('高龄产妇（≥35岁）');
    }
  }
  
  // 可以添加更多风险因素的检查
  // 如：既往病史、多胎妊娠等
  
  return {
    isHighRisk: riskFactors.length > 0,
    riskFactors
  };
}